import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FaUserAlt, FaPhoneAlt, FaLock, FaEye, FaEyeSlash, FaEnvelope } from "react-icons/fa";
import "./signup.css";
import templeIcon from "../../assets/images/temple_sign.png";
import { insertSignUpDetails } from "../../services/auth.service";
import Swal from "sweetalert2";

// UTF-8 safe Base64 encoder (same output as btoa for normal characters)
const toBase64 = (str) => {
    const bytes = new TextEncoder().encode(str);
    let binary = "";
    bytes.forEach((b) => (binary += String.fromCharCode(b)));
    return btoa(binary);
};

const SignupComponent = () => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);

    const initialValues = {
        devoteeName: "",
        mobileNo: "",
        email: "",
        password: "",
        confirmPassword: "",
    };

    const validationSchema = Yup.object({
        devoteeName: Yup.string().trim().required("Devotee name is required"),
        mobileNo: Yup.string()
            .matches(/^[0-9]{10}$/, "Enter a valid 10-digit number")
            .required("Mobile number is required"),
        email: Yup.string()
            .trim()
            .email("Enter a valid email address")
            .required("Email address is required"),
        password: Yup.string()
            .required("Password is required")
            .min(8, "Password must be at least 8 characters")
            .matches(/[A-Z]/, "Must include at least one uppercase letter")
            .matches(/[a-z]/, "Must include at least one lowercase letter")
            .matches(/[0-9]/, "Must include at least one number")
            .matches(/[@$!%*?&]/, "Must include at least one special character"),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref("password"), null], "Passwords do not match")
            .required("Confirm password is required"),
    });

    const handleSubmit = async (values, { resetForm }) => {
        const dto = {
            devoteeName: values.devoteeName.trim(),
            mobileNo: values.mobileNo.trim(),
            email: values.email.trim().toLowerCase(),
            password: toBase64(values.password),
            confirmPassword: toBase64(values.confirmPassword),
        };

        try {
            const response = await insertSignUpDetails(dto);

            if (response.success) {
                Swal.fire({
                    title: "Success!",
                    text: response.message || "Signup successful! Welcome email sent to your registered email.",
                    icon: "success",
                    confirmButtonColor: "#3085d6",
                }).then(() => {
                    resetForm();
                    navigate("/");
                });
            } else {
                Swal.fire({
                    title: "Warning",
                    text: response.message || "Signup failed",
                    icon: "warning",
                    confirmButtonColor: "#f27474",
                });
            }
        } catch (error) {
            console.error("Signup error:", error);

            let msg = "Something went wrong, please try again!";
            if (error.response && error.response.data) {
                msg = error.response.data.message || msg;
            }

            Swal.fire({
                title: "Error",
                text: msg,
                icon: "error",
                confirmButtonColor: "#d33",
            });
        }
    };

    return (
        <div className="signup-page">
            <div className="signup-box container">
                <div className="row signup-card shadow-lg rounded-4 overflow-hidden g-0">
                    {/* LEFT FORM SECTION */}
                    <div className="col-12 col-lg-7 form-side p-3 p-sm-4 p-md-5">
                        <div className="text-center mb-4">
                            <h3 className="fw-bold text-dark signup-title">
                                <span className="text-warning">Create</span> Your Account
                            </h3>
                            <p className="text-muted small mb-0">✨ Begin your spiritual journey ✨</p>
                        </div>

                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ isSubmitting }) => (
                                <Form noValidate>
                                    <div className="row g-3 g-md-4 text-start">
                                        {/* Devotee Name */}
                                        <div className="col-12 col-sm-6">
                                            <label className="signup-label">Devotee Name</label>
                                            <div className="signup-input-box">
                                                <FaUserAlt className="signup-icon" />
                                                <Field
                                                    type="text"
                                                    name="devoteeName"
                                                    className="signup-input"
                                                    placeholder="Enter full name"
                                                />
                                            </div>
                                            <ErrorMessage name="devoteeName" component="div" className="signup-error" />
                                        </div>

                                        {/* Mobile No */}
                                        <div className="col-12 col-sm-6">
                                            <label className="signup-label">Mobile Number</label>
                                            <div className="signup-input-box">
                                                <FaPhoneAlt className="signup-icon" />
                                                <Field
                                                    type="text"
                                                    name="mobileNo"
                                                    inputMode="numeric"
                                                    maxLength="10"
                                                    className="signup-input"
                                                    placeholder="Enter mobile number"
                                                />
                                            </div>
                                            <ErrorMessage name="mobileNo" component="div" className="signup-error" />
                                        </div>

                                        {/* Email */}
                                        <div className="col-12">
                                            <label className="signup-label">Email</label>
                                            <div className="signup-input-box">
                                                <FaEnvelope className="signup-icon" />
                                                <Field
                                                    type="email"
                                                    name="email"
                                                    className="signup-input"
                                                    placeholder="Enter email address"
                                                />
                                            </div>
                                            <ErrorMessage name="email" component="div" className="signup-error" />
                                        </div>

                                        {/* Password */}
                                        <div className="col-12 col-sm-6">
                                            <label className="signup-label">Password</label>
                                            <div className="signup-input-box">
                                                <FaLock className="signup-icon" />
                                                <Field
                                                    type={showPassword ? "text" : "password"}
                                                    name="password"
                                                    className="signup-input"
                                                    placeholder="Minimum 8 characters"
                                                />
                                                <span
                                                    className="signup-eye"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                >
                                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                                </span>
                                            </div>
                                            <ErrorMessage name="password" component="div" className="signup-error" />
                                        </div>

                                        {/* Confirm Password */}
                                        <div className="col-12 col-sm-6">
                                            <label className="signup-label">Confirm Password</label>
                                            <div className="signup-input-box">
                                                <FaLock className="signup-icon" />
                                                <Field
                                                    type={showConfirm ? "text" : "password"}
                                                    name="confirmPassword"
                                                    className="signup-input"
                                                    placeholder="Re-enter password"
                                                />
                                                <span
                                                    className="signup-eye"
                                                    onClick={() => setShowConfirm(!showConfirm)}
                                                >
                                                    {showConfirm ? <FaEyeSlash /> : <FaEye />}
                                                </span>
                                            </div>
                                            <ErrorMessage name="confirmPassword" component="div" className="signup-error" />
                                        </div>
                                    </div>

                                    <div className="text-center">
                                        <button type="submit" disabled={isSubmitting} className="signup-btn mt-4">
                                            {isSubmitting ? "Signing Up..." : "Sign Up"}
                                        </button>
                                    </div>

                                    <div className="text-center mt-3">
                                        <small>Already have an account?</small>{" "}
                                        <button type="button" className="signup-link" onClick={() => navigate("/")}>
                                            Sign In
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>

                    {/* RIGHT IMAGE SECTION (desktop only) */}
                    <div className="col-lg-5 d-none d-lg-flex align-items-center justify-content-center image-side">
                        <div className="temple-glow"></div>
                        <img src={templeIcon} alt="Temple" className="temple-animated" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SignupComponent;